import React, { useEffect, useState } from 'react'
import {
    MultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
} from 'firebase/auth'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { Box, Center } from '@chakra-ui/react'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import SharedHelpText from '~components/shared/form/shared-help-text'
import CodeInput from '~components/shared/form/code-input'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'

interface Props {
    verificationId: string | undefined
    resolver: MultiFactorResolver | undefined
    isOpen: boolean
    onCancel: () => void
    /** Called after the second factor is successfully verified. */
    onVerified?: () => void
}

export default function MFAModal({
    verificationId,
    resolver,
    isOpen,
    onCancel,
    onVerified,
}: Props) {
    const { onCompleted, onError } = useToastFeedback(
        'Your log in was successful!',
        'There was a problem verifying your log in. Please try again.'
    )

    const [code, setCode] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)

    async function handleVerify() {
        if (!code || !verificationId || !resolver) {
            onError()
            return
        }

        setLoading(true)
        try {
            const cred = PhoneAuthProvider.credential(verificationId, code)
            const multiFactorAssertion =
                PhoneMultiFactorGenerator.assertion(cred)
            await resolver.resolveSignIn(multiFactorAssertion)
            onVerified?.()
        } catch (e) {
            console.error({
                message: 'Error verifying code',
                error: e,
            })
            onError()
        } finally {
            onCompleted()
            setLoading(false)
        }
    }

    return (
        <SharedModal
            title="You need to verify your login"
            isOpen={isOpen}
            onClose={() => {}}
            onCancel={onCancel}
            onConfirm={handleVerify}
            isLoading={loading}
            isConfirmDisabled={code.length !== 6}
            confirmText={loading ? 'Verifying...' : 'Verify'}
        >
            <SharedStack>
                <Box>
                    <SharedSectionHeader>
                        We've sent a code to {/* @ts-ignore */}
                        {resolver?.hints?.at(0)?.phoneNumber || 'your phone'}
                    </SharedSectionHeader>
                    <SharedHelpText>
                        Please enter the code below to verify your login.
                    </SharedHelpText>
                </Box>
                <Center w="full">
                    <CodeInput
                        value={code}
                        onChange={setCode}
                        onSubmit={handleVerify}
                    />
                </Center>
            </SharedStack>
        </SharedModal>
    )
}
